import { useEffect, useRef, useState, type ReactNode, type ReactElement, type RefObject, type Dispatch, type SetStateAction } from 'react';

interface ArticleOverflowProps {
  kind: 'equation' | 'table';
  label: string;
  hint: string;
  children: ReactNode;
}

/** Keep wide content locally scrollable, with a keyboard stop only when needed. */
export function ArticleOverflow({ kind, label, hint, children }: ArticleOverflowProps): ReactElement {
  const scrollRef: RefObject<HTMLElement | null> = useRef<HTMLElement | null>(null);
  const [overflows, setOverflows]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false);
  const Container: 'span' | 'div' = kind === 'equation' ? 'span' : 'div';

  useEffect((): (() => void) | undefined => {
    const element: HTMLElement | null = scrollRef.current;
    if (!element) return;
    const measure = (): void => setOverflows(element.scrollWidth > element.clientWidth + 1);
    const observer: ResizeObserver = new ResizeObserver(measure);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    measure();
    return (): void => observer.disconnect();
  }, [children]);

  return (
    <Container className={`article-${kind}`}>
      <Container ref={(element: HTMLElement | null): void => { scrollRef.current = element; }} className="article-overflow" tabIndex={overflows ? 0 : undefined} role={overflows ? 'region' : undefined} aria-label={overflows ? label : undefined}>
        {children}
      </Container>
      {overflows && <span className="article-scroll-hint" aria-hidden="true">↔ {hint}</span>}
    </Container>
  );
}
